import { Component } from '@angular/core';
import { NumberSuffixPipe } from '../filters/numberSuffix-pipe';
@Component({
  template: '',
})
export class ChartService {
  chartOption = {};

  loadWaterFall(
    waterfallData,
    startingPoint,
    title,
    customFirstBarColor?: string,
    yAxisFractionDigits = 1
  ) {
    this.chartOption = {
      title: {
        text: title,
        left: 'center',
        textStyle: {
          color: '#000000',
          fontSize: '14px',
        },
      },

      tooltip: {
        show: true,
        trigger: 'item',
        borderColor: 'white',
        responsive: true,
        backgroundColor: '#fafcfe',
        formatter: (params) => {
          let value;
          if (params.data[4] == 0) {
            value = params.data[2];
          } else {
            value = params.data[2] - params.data[1];
          }

          const labelData = this.formatFullValue(value);
          return params.data[0] + '<br/>' + 'Value' + ' : ' + labelData;
        },
      },
      grid: {
        left: '0%',
        right: '3%',
        bottom: '3%',
        top: 48,
        containLabel: true,
      },
      dataset: {
        source: waterfallData,
      },
      xAxis: {
        type: 'category',
        Animation: true,
        animationThreshold: 2000,
        animationDuration: 1000,
        animationDurationUpdate: 80,
        animationEasing: 'cubicOut',
        animationDelay: 100,
        animationEasingUpdate: '300',
        splitLine: { show: false },
        axisLabel: {
          interval: 0,
          //rotate: 5,
          fontSize: 11.5,
          color: 'black',
          fontWeight: 500,
        },
        axisLine: {
          show: false,
        },
        axisTick: {
          show: false,
        },
      },
      yAxis: {
        type: 'value',
        Animation: true,
        animationThreshold: 2000,
        animationDuration: 1000,
        animationDurationUpdate: 80,
        animationEasing: 'cubicOut',
        animationDelay: 100,
        animationEasingUpdate: '300',
        min: startingPoint,
        // Leave room above the tallest bar so value labels are not clipped.
        max: (extent) => {
          const span = extent.max - extent.min;
          const pad = span === 0 ? Math.abs(extent.max) * 0.12 : span * 0.12;
          return extent.max + (pad || 1);
        },
        splitLine: { show: true },
        axisLabel: {
          formatter: function (xAxis) {
            let label = NumberSuffixPipe.prototype.transform(xAxis, yAxisFractionDigits);
            var length = label.length;

            var tempStr;
            if (length === 5) {
              tempStr = label + ' ';
              return tempStr;
            } else if (length === 4) {
              tempStr = label + '  ';
              return tempStr;
            } else if (length === 3) {
              tempStr = label + '   ';
              return tempStr;
            } else if (length === 2) {
              tempStr = label + '    ';
              return tempStr;
            } else if (length === 1) {
              tempStr = label + '      ';
              return tempStr;
            } else {
              return label;
            }
          },
          align: 'center',
          fontSize: 12,
          margin: 20,
          borderWidth: 20,
        },
        axisLine: {
          lineStyle: {
            type: 'dotted',
          },
          onZero: false,
        },
      },
      series: [
        {
          type: 'custom',
          // Drawn inside renderItem. The series label is clipped on UAT when
          // bars sit against the top of the axis.
          label: { show: false },
          clip: false,
          datasetIndex: 0,
          encode: {
            y: 2,
          },

          renderItem: (params, api) => {
            const category = api.value(0);
            const barStartValue = Number(api.value(1));
            const barEndValue = Number(api.value(2));
            const startCoord = api.coord([category, barStartValue]);
            const endCoord = api.coord([category, barEndValue]);
            const rectWidth = 45;
            const rectMinHeight = 4;

            const topY = Math.min(startCoord[1], endCoord[1]);
            const bottomY = Math.max(startCoord[1], endCoord[1]);
            const goingDown = barEndValue < barStartValue;
            let rectHeight = bottomY - topY;
            if (!Number.isFinite(rectHeight) || rectHeight < rectMinHeight) {
              rectHeight = rectMinHeight;
            }

            const style = api.style();
            if (goingDown) {
              style.fill = '#FF0404';
            } else {
              style.fill = '#009b19';
            }
            const referenceData = api.value(3);
            if (referenceData === 1) {
              if (category === 0 && customFirstBarColor) {
                style.fill = customFirstBarColor;
              } else {
                style.fill = '#c4c4c4';
              }
            }
            if (referenceData === 2) {
              style.fill = '#DCDCDC';
            }

            const marker = api.value(4);
            const labelValue = marker == 0 ? barEndValue : barEndValue - barStartValue;

            return {
              type: 'group',
              children: [
                {
                  type: 'rect',
                  shape: {
                    x: endCoord[0] - rectWidth / 2,
                    y: topY,
                    width: rectWidth,
                    height: rectHeight,
                  },
                  style: { fill: style.fill },
                },
                {
                  type: 'text',
                  silent: true,
                  style: {
                    text: this.formatMillions(labelValue),
                    x: endCoord[0],
                    y: topY - 4,
                    textAlign: 'center',
                    textVerticalAlign: 'bottom',
                    fill: '#000000',
                    fontWeight: 'bold',
                    fontSize: 12,
                  },
                },
              ],
            };
          },
        },
      ],
    };
    return this.chartOption;
  }

  private formatMillions(value: number, decimals: number = 1): string {
    const numeric = typeof value === 'number' ? value : Number(value);
    if (!Number.isFinite(numeric)) {
      return '0.0M';
    }
    const divisor = 1_000_000;
    const factor = Math.pow(10, decimals);
    const rounded = Math.round((numeric / divisor) * factor) / factor;
    return `${rounded.toFixed(decimals)}M`;
  }

  private formatFullValue(value: number): string {
    if (value === null || value === undefined) {
      return '0';
    }
    
    // Return the full value without rounding or formatting as millions
    return value.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  }
}
